// SunTracker.ts
import * as THREE from 'three';
import SunCalc from 'suncalc';
import { EnergyTracker } from './tracker/EnergyTracker';
import type { PanelType, SolarTrackerPanel } from './tracker/types/SolarTrackerPanel';

export type { SolarTrackerPanel } from './tracker/types/SolarTrackerPanel';

export type SunTrackerOptions = {
  worldNorthOffsetDeg?: number;     // Szene relativ zu echtem Norden (CW)
  radius?: number;                  // Abstand der Sonne in der Szene
  mirrorEastWest?: boolean;         // E/W spiegeln
  limitsByType?: Partial<Record<PanelType, {
    coneFrontDeg?: number;
    coneBackDeg?: number;
    coneLeftDeg?: number;
    coneRightDeg?: number;
    singlePitchPosDeg?: number;
    singlePitchNegDeg?: number;
  }>>;
};

const toRad = THREE.MathUtils.degToRad;
const toDeg = THREE.MathUtils.radToDeg;

// EINZIGE zentrale Stelle: Standard-Limits pro Typ
const TYPE_LIMITS: Record<PanelType, {
  // Kegel-Limit (gleicher Winkel in alle Richtungen, relativ zur Panel-Normale)
  coneRad?: number;
  // Single-Achse Limits (relativ zur Grundpose um die Rotationsachse)
  singlePitchPosRad?: number;
  singlePitchNegRad?: number;
}> = {
  fixed:     { coneRad: 0 },
  limited:   { coneRad: toRad(30) },     // ±30°
  unlimited: { coneRad: toRad(90) },     // ±90°
  dual:      { coneRad: Math.PI },       // „alles“ (falls du willst, kannst du das später verkleinern)
  single:    { singlePitchPosRad: toRad(30), singlePitchNegRad: toRad(30) }, // ±30°
};

// kleine Utils
function clampSigned(angleRad: number, posRad: number, negRad: number) {
  return Math.min(Math.max(angleRad, -negRad), posRad);
}

export class SunTracker {
  private trackerMap: Map<string, SolarTrackerPanel>;
  private sunLightRef: React.RefObject<THREE.DirectionalLight>;
  private sunSphereRef: React.RefObject<THREE.Mesh>;
  private energyTracker: EnergyTracker;
  private lastUpdateTime: Date;

  private mirrorEastWest: boolean;
  private worldNorthOffsetDeg: number;
  private radius: number;
  private limitsByType?: SunTrackerOptions['limitsByType'];

  constructor(
    trackerMap: Map<string, SolarTrackerPanel>,
    sunLightRef: React.RefObject<THREE.DirectionalLight>,
    sunSphereRef: React.RefObject<THREE.Mesh>,
    energyTracker: EnergyTracker,
    opts: SunTrackerOptions = {}
  ) {
    this.trackerMap = trackerMap;
    this.sunLightRef = sunLightRef;
    this.sunSphereRef = sunSphereRef;
    this.energyTracker = energyTracker;
    this.lastUpdateTime = new Date();

    this.worldNorthOffsetDeg = opts.worldNorthOffsetDeg ?? 0;
    this.radius = opts.radius ?? 300;
    this.mirrorEastWest = !!opts.mirrorEastWest;
    this.limitsByType = opts.limitsByType;
  }

  forPanels(panels: Map<string, SolarTrackerPanel>, energy: EnergyTracker) {
    return new SunTracker(panels, this.sunLightRef, this.sunSphereRef, energy, {
      worldNorthOffsetDeg:this.worldNorthOffsetDeg, radius:this.radius,
      mirrorEastWest:this.mirrorEastWest, limitsByType:this.limitsByType,
    });
  }

  resetLastUpdateTime(value: Date | number) {
    this.lastUpdateTime = value instanceof Date ? new Date(value) : new Date(value);
  }

  /** Position aus Azimut (0=N,90=E) & Elevation in Grad → +Z=N, +X=E */
  private posFromAzEl(azFromNorthDeg: number, altDeg: number): THREE.Vector3 {
    const a = toRad(azFromNorthDeg + this.worldNorthOffsetDeg);
    const e = toRad(altDeg);
    const s = this.mirrorEastWest ? -1 : 1;
    const x = s * this.radius * Math.sin(a) * Math.cos(e); // Ost-West
    const y =     this.radius * Math.sin(e);               // Höhe
    const z =     this.radius * Math.cos(a) * Math.cos(e); // Nord-Süd
    return new THREE.Vector3(x, y, z);
  }

  /** Sonnenrichtung berechnen und optional Light/Sphere positionieren. */
  private computeSunDirection(date: Date, lat: number, lon: number, updateVisuals = true): THREE.Vector3 {
    const { altitude, azimuth } = SunCalc.getPosition(date, lat, lon);
    // SunCalc: azimuth 0 rad = Süd, + nach Westen → umrechnen auf 0=N (CW)
    const altDeg = toDeg(altitude);
    const azFromNorthDeg = (toDeg(azimuth) + 180) % 360;

    const p = this.posFromAzEl(azFromNorthDeg, altDeg);

    if (updateVisuals) {
      this.sunSphereRef.current?.position.copy(p);
      this.sunLightRef.current?.position.copy(p);
    }

    return p.clone().normalize();
  }

  private getAlignmentQ(deg: number): THREE.Quaternion {
    return new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), toRad(deg));
  }

  /** yaw = 360° − alignment (Ausrichtung relativ zu +Z=N) */
  private getYawQ(alignmentDeg: number): THREE.Quaternion {
    const a = ((alignmentDeg ?? 0) % 360 + 360) % 360;
    const yawRad = toRad(360 - a);
    return new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawRad);
  }

  /** Parken/Unparken: Y-Translation relativ zur Basisposition */
  private parkLimited(panel: SolarTrackerPanel, on: boolean) {
    const base = panel.basePosition;
    const drop = panel.parkDrop ?? 0;
    const lift = (panel as any).activeLift ?? 0;
    const enabled = panel.parkOnNoSun ?? false;
    if (!enabled || !base || (drop <= 0 && lift <= 0)) return;

    // Idempotent: Position wird jeden Tick aus dem Zustand gesetzt (statt nur
    // beim Übergang). Das heilt Drift und macht die Anhebung auch direkt nach
    // dem Platzieren wirksam. Bei Sonne fährt der Tracker um activeLift über
    // die Basis hoch (aktive Tracking-Höhe), nachts parkt er um parkDrop tiefer.
    if (on) {
      panel.position.set(base.x, base.y - drop, base.z);
      panel.isParked = true;
    } else {
      panel.position.set(base.x, base.y + lift, base.z);
      panel.isParked = false;
    }
  }

  /** EINZIGE Funktion, die Limits liefert (inkl. optionaler Panel-Overrides) */
  private getUnifiedLimits(panel: SolarTrackerPanel) {
    const type: PanelType = panel.type ?? 'fixed';
    const base = TYPE_LIMITS[type];
    const configured = this.limitsByType?.[type];

    // Optional: per-Panel Overrides ermöglichen (falls gesetzt)
    // Für „cone“ nehmen wir symmetrisch denselben Winkel für alle Seiten:
    const configuredConeDeg = configured
      ? Math.max(
          configured.coneFrontDeg ?? 0,
          configured.coneBackDeg ?? 0,
          configured.coneLeftDeg ?? 0,
          configured.coneRightDeg ?? 0
        )
      : undefined;
    const coneRad =
      configuredConeDeg != null && configuredConeDeg > 0
        ? toRad(configuredConeDeg)
        : base.coneRad ??
      (panel.coneLimitPosDeg != null ||
       panel.coneLimitNegDeg != null ||
       panel.coneLimitLeftDeg != null ||
       panel.coneLimitRightDeg != null
        ? toRad(
            Math.max(
              panel.coneLimitPosDeg ?? 0,
              panel.coneLimitNegDeg ?? 0,
              panel.coneLimitLeftDeg ?? 0,
              panel.coneLimitRightDeg ?? 0
            )
          )
        : 0);

    // Für single-Achse: ±
    const singlePosRad = panel.pitchLimitPosDeg != null
      ? toRad(panel.pitchLimitPosDeg)
      : (configured?.singlePitchPosDeg != null ? toRad(configured.singlePitchPosDeg) : (base.singlePitchPosRad ?? 0));

    const singleNegRad = panel.pitchLimitNegDeg != null
      ? toRad(panel.pitchLimitNegDeg)
      : (configured?.singlePitchNegDeg != null ? toRad(configured.singlePitchNegDeg) : (base.singlePitchNegRad ?? 0));

    return { coneRad, singlePosRad, singleNegRad };
  }

  /** robuste Rotationshilfe für Kegel-Tracking (auch antiparallel) */
  private quatTowardWithLimit(
    from: THREE.Vector3,      // z.B. Panel-Normale (+Y)
    to: THREE.Vector3,        // Zielrichtung (norm.)
    maxAngleRad: number,      // Limitwinkel
    fallbackAxis: THREE.Vector3 // z.B. +X
  ): THREE.Quaternion {
    const q = new THREE.Quaternion();
    const dot = THREE.MathUtils.clamp(from.dot(to), -1, 1);
    let angle = Math.acos(dot); // 0..π
    if (angle < 1e-6) return q.identity();

    let axis = new THREE.Vector3().crossVectors(from, to);
    const len = axis.length();
    if (len < 1e-6) {
      axis = fallbackAxis.clone().normalize(); // antiparallel
    } else {
      axis.multiplyScalar(1 / len);
    }
    angle = Math.min(angle, maxAngleRad);
    return q.setFromAxisAngle(axis, angle);
  }

  /** Nachführlogik */
  private rotatePanel(panel: SolarTrackerPanel, sunDirWorld: THREE.Vector3) {
    // Digital-twin mode: use real-world angle directly instead of sun calculation
    if (panel.liveAngleDeg != null) {
      const alignmentDeg = panel.alignment ?? 0;
      const mountingAxisName: 'x' | 'z' = panel.mountingAxis ?? 'x';
      const alignmentQ = this.getAlignmentQ(alignmentDeg);
      const axis = (mountingAxisName === 'z') ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
      const liveQ = new THREE.Quaternion().setFromAxisAngle(axis, toRad(panel.liveAngleDeg));
      panel.setRotationFromQuaternion(alignmentQ.multiply(liveQ));
      return;
    }

    const type: PanelType = panel.type ?? 'fixed';
    const alignmentDeg = panel.alignment ?? 0;
    const mountingDeg = panel.mountingDeg ?? 0;
    const mountFirst   = panel.mountFirst ?? false;
    const mountingAxisName: 'x' | 'z' = panel.mountingAxis ?? 'x';
    const mountFlip = panel.mountFlip ?? false;
    const yawDeg = (panel as any).yawDeg as number | null | undefined;

    let baseQ: THREE.Quaternion;
    if (yawDeg != null) {
      // Neuer, expliziter Pfad (Builder-Tracker): erst Yaw auf den Ziel-Azimut,
      // dann Kippung um die EIGENE Rack-X-Achse. Ergibt für jeden Azimut ein
      // gerades, unverdrehtes Gestell (Normal-Azimut = yawDeg, numerisch
      // verifiziert). Der Legacy-Pfad darunter erzeugte bei Ost/West und freien
      // Rotationen einen In-Plane-Twist — das Rack sah "schief" aus.
      baseQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), toRad(yawDeg))
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), toRad(mountingDeg)));
    } else {
      // Legacy-Pfad: von Hand kalibrierte Siedlungs-/Einzelhaus-Konfigurationen
      const alignmentQ = this.getAlignmentQ(alignmentDeg);

      const axisLocal =
        (mountingAxisName === 'z') ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);

      const axisWithSide = mountFlip ? axisLocal.clone().negate() : axisLocal.clone();
      // wenn Align zuerst, dreht sich die Achse mit
      const axisForMount = mountFirst ? axisWithSide : axisWithSide.applyQuaternion(alignmentQ);

      const mountingQ = new THREE.Quaternion().setFromAxisAngle(axisForMount, toRad(mountingDeg));

      // Basispose (Alignment ⨯ Mounting)  → mountingDeg ist damit die Ausgangslage
      baseQ = mountFirst
        ? mountingQ.clone().multiply(alignmentQ)   // Mount → Align
        : alignmentQ.clone().multiply(mountingQ);  // Align → Mount
    }

    // Sonne in Panel-Lokalraum (Basispose invertieren)
    const sunDirPanel = sunDirWorld.clone().applyQuaternion(baseQ.clone().invert());

    // Nacht? -> ggf. parken und nur Basispose setzen
    if (sunDirWorld.y <= 0) {
      if (type === 'limited' || type === 'unlimited') this.parkLimited(panel, true);
      panel.setRotationFromQuaternion(baseQ);
      return;
    } else {
      if (type === 'limited' || type === 'unlimited') this.parkLimited(panel, false);
    }

    const panelNormalLocal = new THREE.Vector3(0, 1, 0);
    const sideAxisLocal    = new THREE.Vector3(1, 0, 0); // +X = rechts
    let trackingQ: THREE.Quaternion | null = null;

    try {
      const target = sunDirPanel.clone().normalize();
      const limits = this.getUnifiedLimits(panel);

      switch (type) {
        case 'single': {
          // Einachs-Tracking (relativ zur Grundpose) um lokale X oder Z:
          const singleAxis =
            (mountingAxisName === 'z') ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);

          const projected = target.clone().projectOnPlane(singleAxis);
          if (projected.lengthSq() < 1e-12) {
            trackingQ = new THREE.Quaternion(); // Sonne liegt auf Achse -> keine sinnvolle Änderung
            break;
          }
          projected.normalize();

          // Winkel (0..π) zwischen Panel-Normale (+Y) und projizierter Sonne
          let angle = panelNormalLocal.angleTo(projected);

          // Vorzeichen um singleAxis
          const cross = new THREE.Vector3().crossVectors(panelNormalLocal, projected);
          const sign  = Math.sign(cross.dot(singleAxis));
          angle *= (sign || 1);

          // **einzige** Clamp-Quelle für single: zentrale ±30° (oder Panel-Override)
          const clamped = clampSigned(angle, limits.singlePosRad ?? 0, limits.singleNegRad ?? 0);
          trackingQ = new THREE.Quaternion().setFromAxisAngle(singleAxis, clamped);
          break;
        }

        case 'limited':
        case 'unlimited':
        case 'dual': {
          // Kegel-Tracking mit **einem** zentralen Winkel (±cone)
          const lim = limits.coneRad ?? 0;
          trackingQ = this.quatTowardWithLimit(panelNormalLocal, target, lim, sideAxisLocal);
          // Yaw-Anteil (Twist um die Panel-Normale +Y) entfernen — Swing-Twist-
          // Zerlegung. Ohne das dreht sich das GANZE Gestell zur Sonnen-Azimut
          // und radiert die platzierte Rotation/Ausrichtung optisch aus; real
          // kippt nur der Paneltisch. Die Panel-Normale (und damit die Energie-
          // berechnung) ändert sich durch das Entfernen des Twists exakt NICHT:
          // eine Rotation um die Normale lässt die Normale unverändert.
          const twist = new THREE.Quaternion(0, trackingQ.y, 0, trackingQ.w);
          const twistLen = Math.hypot(twist.y, twist.w);
          if (twistLen > 1e-8) {
            twist.y /= twistLen; twist.w /= twistLen;
            trackingQ = trackingQ.clone().multiply(twist.invert());
          }
          break;
        }

        case 'fixed':
        default: {
          panel.setRotationFromQuaternion(baseQ);
          return;
        }
      }

      // Weltpose: Basis (Alignment⨯Mounting) * Tracking (im Panel-Lokalraum berechnet)
      panel.setRotationFromQuaternion(baseQ.clone().multiply(trackingQ!));
    } catch (err) {
      console.warn(`⚠️ Fehler beim Rotieren des Panels "${panel.name ?? 'unnamed'}":`, err);
      panel.setRotationFromQuaternion(baseQ);
    }
  }

  update(date: Date, lat: number, lon: number, opts?: { skipVisual?: boolean }) {
    // skipVisual only suppresses sun light/sphere updates. Panel quaternions still
    // update because the energy model depends on the tracked panel orientation.
    const sunDirWorld = this.computeSunDirection(date, lat, lon, !opts?.skipVisual);

    this.trackerMap.forEach((panel) => {
      if (!panel) return;
      this.rotatePanel(panel, sunDirWorld);
    });

    // Energie-Update
    const now = date;
    const deltaHours = (now.getTime() - this.lastUpdateTime.getTime()) / 3_600_000;
    if (!Number.isFinite(deltaHours)) {
      this.lastUpdateTime = now;
      return;
    }

    if (deltaHours > 0 && deltaHours <= 1) {
      this.energyTracker.updateAll?.(sunDirWorld, deltaHours, date);
    }

    this.lastUpdateTime = now;
  }
}
